import { Calendar, User, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { mockBlogPosts } from '@/data/mockData';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';

const categoryLabels = {
  'conseils-cv': 'CV & Lettres',
  'preparation-entretiens': 'Entretiens',
  'developpement-competences': 'Compétences',
  'portraits-talents': 'Portraits',
  'innovation-tech': 'Innovation & Tech'
};

const categoryColors = {
  'conseils-cv': 'bg-primary',
  'preparation-entretiens': 'bg-secondary',
  'developpement-competences': 'bg-success',
  'portraits-talents': 'bg-accent',
  'innovation-tech': 'bg-cyan-500'
};

export function BlogSection() {
  const latestPosts = mockBlogPosts.slice(0, 3);
  const sectionRef = useScrollAnimation();

  return (
    <section ref={sectionRef} className="section-padding bg-gradient-to-b from-background via-primary-light/10 to-background scroll-reveal">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-20 max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-foreground mb-8 tracking-tight leading-tight">
            Conseils & Actualités
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground font-light leading-relaxed">
            Des conseils pratiques et des histoires inspirantes pour booster votre carrière
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {latestPosts.map((post, index) => (
            <Card 
              key={post.id} 
              className="group glass hover-lift hover-glow border border-border/50 hover:border-primary/60 transition-all duration-500 overflow-hidden rounded-[2rem] animate-fade-in-up hover:rotate-1"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Cover Image */}
              <div className="relative h-64 bg-gradient-to-br from-primary-light/30 to-muted/30 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent group-hover:scale-110 group-hover:rotate-2 transition-all duration-700"></div>
                
                {/* Category Badge */}
                <div className={`absolute top-5 left-5 ${categoryColors[post.category]} text-white px-4 py-2 rounded-xl text-xs font-bold shadow-lg backdrop-blur-sm`}>
                  {categoryLabels[post.category]}
                </div>
                
                {/* Elegant Letter */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10rem] font-extrabold text-primary/10 group-hover:scale-110 transition-transform duration-700">
                    {post.title.charAt(0)}
                  </span>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="font-bold text-2xl text-foreground mb-4 line-clamp-2 group-hover:text-primary transition-colors duration-300 leading-tight">
                  {post.title}
                </h3>
                
                <p className="text-muted-foreground text-base mb-6 line-clamp-3 leading-relaxed font-light">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-6 pb-6 border-b border-border/50">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span className="font-medium">{post.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">
                      {format(post.publishedAt, 'dd MMM', { locale: fr })}
                    </span>
                  </div>
                </div>
                
                <Link to={`/blog/${post.slug}`}>
                  <Button variant="link" className="p-0 h-auto font-bold text-base group/btn hover:scale-105 transition-all">
                    Lire l'article
                    <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-2 transition-transform" />
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-16">
          <Link to="/blog">
            <Button variant="outline" size="xl" className="shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2">
              Voir tous les articles
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}